import React, { useState } from 'react';
import { numberOr } from '../phaseModel';

const RING_RADIUS = 52;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * REHEARSE - spaced repetition of the person's own plan.
 *
 * This is the phase that makes the module training rather than reading, so it
 * has to drill the exact words written in Plan - the cue they chose and the
 * move they wrote, not the module's defaults. Each rep runs cue -> recall ->
 * reveal, because recalling before seeing the answer is the part that sticks.
 *
 * With no plan yet there is nothing to rehearse, and the phase says so and
 * offers the way back rather than drilling placeholder text.
 */
function RehearsePhase({ section, answer, onChange, plan, onReturnToPlan }) {
  const config = section.config || {};
  const target = numberOr(config.targetReps, 5);
  const minReps = numberOr(config.minReps, 1);
  const reps = numberOr(answer && answer.reps, 0);
  const schedule = Array.isArray(config.schedule) ? config.schedule : [];

  // idle -> cued -> revealed, then back to idle with the rep banked.
  const [drill, setDrill] = useState('idle');
  const [announcement, setAnnouncement] = useState('');

  if (!plan) {
    return (
      <div className="tr-card">
        <p style={{ margin: 0 }}>
          There is no plan to rehearse yet. Build your if-then plan first, then come back and drill it.
        </p>
        {onReturnToPlan ? (
          <div style={{ marginTop: '14px' }}>
            <button type="button" className="btn btn-sm btn-primary fw-bold" onClick={onReturnToPlan}>
              Go to my plan
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  const done = Math.min(reps, target);
  const offset = RING_CIRCUMFERENCE * (1 - (target > 0 ? done / target : 0));

  const logRep = () => {
    const next = reps + 1;
    onChange({ reps: next });
    setDrill('idle');
    setAnnouncement(
      'Rep logged. ' +
        next +
        ' of ' +
        target +
        '. ' +
        (next < minReps
          ? minReps - next + ' more to continue.'
          : next < target
          ? 'Keep going, or move on.'
          : 'Fully rehearsed.')
    );
  };

  return (
    <>
      <div className="tr-card">
        <div className="tr-rehearse-grid">
          <div className="tr-ring">
            <svg width="120" height="120" viewBox="0 0 120 120" aria-hidden="true" focusable="false">
              <circle cx="60" cy="60" r={RING_RADIUS} fill="none" stroke="#e1e3ea" strokeWidth="9" />
              <circle
                className="tr-ring-value"
                cx="60"
                cy="60"
                r={RING_RADIUS}
                fill="none"
                stroke="#005268"
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={offset}
                transform="rotate(-90 60 60)"
              />
            </svg>
            <p className="tr-ring-num">
              <b>{reps}</b>
              <span>{'of ' + target + ' reps'}</span>
            </p>
          </div>
          <div>
            <div className="tr-card-label">Rehearsing your plan</div>
            <p style={{ fontSize: '17px', margin: 0 }}>
              When <b style={{ color: '#b1502c' }}>{plan.cue}</b>
              {' → '}
              <b style={{ color: '#003c4d' }}>{plan.action}</b>
            </p>
            <div style={{ marginTop: '14px' }}>
              <button
                type="button"
                className="btn btn-sm btn-primary fw-bold"
                onClick={() => setDrill('cued')}
                disabled={drill !== 'idle'}
              >
                {reps === 0 ? 'Rehearse now' : 'Rehearse again'}
              </button>
            </div>
          </div>
        </div>

        <div role="status" aria-live="polite">
          {drill === 'cued' ? (
            <div className="tr-drill">
              <p className="tr-drill-cue">
                {(config.cuePrompt || 'The cue fires') + ': '}
                <b>{plan.cue}</b>
              </p>
              <p className="tr-help" style={{ marginBottom: '12px' }}>
                Say your move to yourself before you look.
              </p>
              <button
                type="button"
                className="btn btn-sm btn-secondary fw-bold"
                onClick={() => setDrill('revealed')}
              >
                What is my move?
              </button>
            </div>
          ) : null}

          {drill === 'revealed' ? (
            <div className="tr-drill">
              <p className="tr-drill-cue">
                {(config.cuePrompt || 'The cue fires') + ': '}
                <b>{plan.cue}</b>
              </p>
              <p className="tr-drill-reveal">{'→ ' + plan.action}</p>
              <button type="button" className="btn btn-sm btn-primary fw-bold" onClick={logRep}>
                Rehearsed it
              </button>
            </div>
          ) : null}

          <p className="tr-visually-hidden">{announcement}</p>
        </div>
      </div>

      {schedule.length > 0 ? (
        <div className="tr-card">
          <div className="tr-card-label">Your spaced schedule</div>
          <ul className="tr-schedule">
            {schedule.map((item, index) => {
              const isDone = index < reps;
              return (
                <li
                  className={'tr-sched-item' + (isDone ? ' is-done' : '')}
                  key={item.id || item.when || index}
                >
                  <span className="tr-sched-dot" aria-hidden="true" />
                  <span className="tr-sched-when">{item.when}</span>
                  <span className="tr-sched-desc">{item.description}</span>
                  <span className="tr-visually-hidden">{isDone ? ' (done)' : ' (not yet)'}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </>
  );
}

export default RehearsePhase;
